/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react'
import { navigate, useParams } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'

import { Grid, Typography } from '@mui/material'

import { Button, Loader } from '@refera/ui-web'
import { FormProvider, useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { useBudgetForm } from '../../hooks/use-budget-form'

import { DocumentDownload } from '@refera/ui-icons'
import Theme from '@refera/ui-core'

import {
  ProblemField,
  SolutionField,
  DiagnosisField,
  WarrantyMonthsField,
  MandatoryServiceField,
  MaterialTable,
  CompartmentField,
} from './fields'

import HeaderTitle from '_/components/header-title'
import { getBudgetByIdSelectorJS } from '_/modules/budget/selectors'
import {
  currentServiceOrderSelector,
  getProblemsListSelector,
} from '_modules/service-orders/selectors'
import { categoriesSelector } from '_modules/categories/selectors'
import { addLeadingZeros } from '_/views/finance/manage-installments/utils/constants'

import Accordion from '_/components/accordion'
import { NumericInput } from '../../components/Inputs/NumericInput'
import {
  ADD_PROBLEM_FORM_DEFAULT_VALUES,
  AI_VALIDATION_MODAL_INITIAL_STATE,
  MATERIAL_TABLE_DEFAULT_ITEM,
} from '../../utils/constants'
import { AiValidationModal } from './AiValidationModal'
import {
  getBudgetById,
  updateBudgetResolution,
  getAiValidation,
  getAiResponse,
} from '_/modules/budget/actions'

import { setCompartmentsList } from '_/modules/service-orders/actions'

import { useToast } from '_/hooks/use-toast'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { addProblemSchema } from '../../utils/schemas'
import { useDialog } from '_/hooks/use-dialog'
import { AttachmentsField } from './fields/attachments'
import {
  formatMaterialsPayload,
  formatDefaultBudgetResolution,
  calculateTotalValue,
  createFormDataBudgetResolution,
} from '../../utils/functions'

import useStyles from './styles'
import { SingleServicePrice } from './fields/single-service-price'
import { ImportServiceModal } from './import-service-modal'
import useRolePermission from '_/hooks/use-role-permission'
import { STEP_STATUS } from '_/utils/constants/service-order'
import { ROUTES } from '_/utils/constants'
import axios from 'axios'

async function fetchFileFromUrl(url, filename) {
  try {
    const { data: blob } = await axios.get(url, { responseType: 'blob' })
    const name = filename || url.split('/').pop()?.split('?')[0] || 'arquivo'
    return new File([blob], name, { type: blob.type })
  } catch {
    return null
  }
}

export function AddProblemPage() {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { serviceOrderId, budgetId } = useParams()
  const { showToast } = useToast()
  const { closeDialog, showDialog } = useDialog()

  const {
    setPage,
    setLoading,
    loading,
    setResetForm,
    budgetResolutionToEdit,
    setBudgetResolutionToEdit,
    addProblemReadOnly,
    setAddProblemReadOnly,
    setIsEditing,
  } = useBudgetForm()

  const budget = useSelector(getBudgetByIdSelectorJS(budgetId))
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const problemsList = useSelector(getProblemsListSelector)
  const categoriesList = useSelector(categoriesSelector)
  const isAgencySaas = serviceOrder?.agency?.contractType === 'SAAS'
  const { isTradesman, isSAAS } = useRolePermission()
  const isMandatoryInfoRef = useRef(true)
  const categoryMandatoryInfo = useRef('')
  const categoryDesiredInfo = useRef('')

  const [modelSlug, setModelSlug] = useState('')
  const [aiValidationModalInfo, setAiValidationModalInfo] = useState(
    AI_VALIDATION_MODAL_INITIAL_STATE
  )
  const [importServiceModalOpen, handleImportServiceModal] = useState(false)
  const [totalValue, setTotalValue] = useState(0)

  const problemNumber = useMemo(() => {
    if (budgetResolutionToEdit) return addLeadingZeros(budgetResolutionToEdit?.index + 1, 2)
    return budget?.budgetResolution?.length
      ? addLeadingZeros(budget?.budgetResolution?.length + 1, 2)
      : '01'
  }, [budget, budgetResolutionToEdit])

  const defaultValues = useMemo(
    () => ({
      ...ADD_PROBLEM_FORM_DEFAULT_VALUES,
      ...formatDefaultBudgetResolution(budgetResolutionToEdit),
      showCompartment: serviceOrder?.vacancy ?? false,
    }),
    [budgetResolutionToEdit, ADD_PROBLEM_FORM_DEFAULT_VALUES, serviceOrder]
  )

  const methods = useForm({
    resolver: yupResolver(addProblemSchema),
    defaultValues,
  })

  const { handleSubmit, setValue, getValues } = methods

  const problem = getValues('problem')

  const referaProblem = useMemo(() => {
    return problemsList?.find(item => item?.id === problem?.value)
  }, [problem?.value, problemsList])

  const isValidInfo = value => value && value !== '<p></p>'

  function handleBack() {
    setAddProblemReadOnly(false)
    setBudgetResolutionToEdit(null)
    // to remove edit parameter from url

    navigate(`${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${budgetId}?edit=true`)

    setPage('default')
  }

  const validateWithAI = useCallback(
    async (solution, fieldName, infoToValidate) => {
      setLoading(true)
      const res = await dispatch(getAiValidation({ solution, fieldName, infoToValidate }))

      setLoading(false)

      return res
    },
    [dispatch]
  )

  const handleImportService = useCallback(async service => {
    const warrantyMonths =
      typeof service.warrantyMonths === 'number' ? String(service.warrantyMonths) : ''

    const problemValue = service.problemText ? { label: service.problemText } : ''

    methods.reset({
      ...ADD_PROBLEM_FORM_DEFAULT_VALUES,
      problem: problemValue,
      problemDescription: service.problemDescription || '',
      serviceDescription: service.serviceDescription || '',
      warrantyMonths,
      isServiceMandatory:
        typeof service.isServiceMandatory === 'boolean' ? String(service.isServiceMandatory) : null,
      laborValue: service.laborValue ?? 0,
      laborTimeHours: service.laborTimeHours ?? 0,
      numberOfProfessionals: service.numberOfProfessionals ?? 0,
      singleServicePrice: service.singleServicePrice ?? 0,
      materials: service.materials?.length ? service.materials : [MATERIAL_TABLE_DEFAULT_ITEM],
      // problemMedia: service.problemMedia?.length ? service.problemMedia : [],
    })

    // Message 72 (success)
    showToast({
      type: 'success',
      message: 'Ação executada com sucesso.',
    })
  }, [])

  const formValues = methods.watch()

  useEffect(() => {
    const calculatedTotal = calculateTotalValue({
      materials: formValues.materials,
      laborValue: formValues.laborValue,
    })
    setTotalValue(calculatedTotal)
  }, [formValues.materials, formValues.laborValue])

  useEffect(() => {
    methods.register('showCompartment')
    setValue('showCompartment', serviceOrder?.vacancy ?? false)
  }, [serviceOrder?.vacancy])

  const hasMandatoryInfo = isValidInfo(referaProblem?.serviceDescriptionMandatoryInfo)
  const hasDesiredInfo = isValidInfo(referaProblem?.serviceDescriptionDesiredInfo)

  const originalSolutionRef = useRef(null)
  const stripTags = html => html?.replace(/<[^>]*>/g, '').trim()

  const handleInfoToValidate = useCallback(
    async (solution, isRecursive = false) => {
      try {
        if (!isRecursive) originalSolutionRef.current = solution

        const validate = async type => {
          const info =
            type === 'description_mandatory_info'
              ? categoryMandatoryInfo.current
              : categoryDesiredInfo.current

          const res = await validateWithAI(solution, type, info)

          if (res?.response === 'ok') {
            return {
              success: true,
              data: null,
            }
          }

          const title = isMandatoryInfoRef.current ? 'Dados necessários' : 'Dados desejados'
          setModelSlug(res?.modelSlug)

          const modalResponse = await new Promise(resolve => {
            setAiValidationModalInfo({
              open: true,
              title,
              message: originalSolutionRef.current,
              label: res?.response,
              handleClick: data => resolve({ success: true, data }),
              handleCancel: () => resolve({ success: false, data: null }),
            })
          })
          return modalResponse
        }

        if (categoryMandatoryInfo.current) {
          const mandatoryValid = await validate('description_mandatory_info')

          if (!mandatoryValid?.success) return false

          if (mandatoryValid?.data?.description) {
            setValue('serviceDescription', mandatoryValid?.data?.description)
            return handleInfoToValidate(mandatoryValid.data?.description, true)
          }
        }

        if (categoryDesiredInfo.current) {
          isMandatoryInfoRef.current = false
          const desiredValid = await validate('description_desired_info')

          if (!desiredValid?.success) return false
          if (desiredValid?.data?.description)
            setValue('serviceDescription', desiredValid?.data?.description)
        }

        isMandatoryInfoRef.current = true
        originalSolutionRef.current = null
        categoryMandatoryInfo.current = ''
        categoryMandatoryInfo.current = ''
        return true
      } catch (error) {
        // Se der erro em ai-validation, continua o fluxo
        return true
      }
    },
    [isMandatoryInfoRef, validateWithAI, originalSolutionRef]
  )

  const setUpCategoryPrompt = () => {
    const categoryNames = serviceOrder?.category
      ?.filter(cat => cat?.name)
      .map(cat => cat.name)
      .join(', ')

    const serviceDescription = getValues('serviceDescription')

    return (
      `Considerando o problema abaixo e entendendo que o chamado já está ` +
      `classificado nas seguintes categorias: ${categoryNames}, ` +
      `indique até 2 categorias com grau de probabilidade acima de 90%, ` +
      `podendo ser as atuais ou não, separando-as por vírgulas:\n\n` +
      `${serviceDescription}\n\n` +
      `Se você não identificar categorias com o grau de probabilidade indicado, ` +
      `retorne apenas "OK", sem nenhum texto adicional.`
    )
  }

  const getInfoVariables = useCallback(
    (newCategories, fieldName, hasInfo) => {
      const data = newCategories
        ?.filter(cat => isValidInfo(cat?.[fieldName]))
        ?.map(cat => stripTags(cat?.[fieldName]))
        .join('\n')

      if (referaProblem && hasInfo) {
        const extra = stripTags(referaProblem?.[fieldName])
        return [data, extra].filter(Boolean).join('\n')
      }
      return data
    },
    [referaProblem]
  )

  const getCategoriesByIds = serviceCategories => {
    if (!serviceCategories?.length) return []

    const ids = new Set(serviceCategories.map(sc => sc.id))
    return categoriesList?.filter(cat => ids.has(cat.get('id'))).map(cat => cat.toJS()) || []
  }

  const getCategoriesByName = names => {
    const categoriesNames = names.split(',').map(cat => cat.trim())
    const selectedCategories = categoriesList?.filter(cat =>
      categoriesNames.includes(cat.get('name'))
    )
    const selectedCategoriesJS = selectedCategories?.map(cat => cat.toJS())
    return selectedCategoriesJS || []
  }

  const processCategories = useCallback(async () => {
    setLoading(true)

    try {
      const res = await dispatch(
        getAiResponse({
          modelSlug: 'category_agent',
          input: setUpCategoryPrompt(),
        })
      )

      let categoriesToValidate = []
      const serviceCategories = serviceOrder?.category.toJS() || []

      if (res?.response.toLowerCase() === 'ok') {
        categoriesToValidate = getCategoriesByIds(serviceCategories)
      } else {
        categoriesToValidate = getCategoriesByName(res?.response)
      }

      categoryMandatoryInfo.current = getInfoVariables(
        categoriesToValidate,
        'serviceDescriptionMandatoryInfo',
        hasMandatoryInfo
      )

      categoryDesiredInfo.current = getInfoVariables(
        categoriesToValidate,
        'serviceDescriptionDesiredInfo',
        hasDesiredInfo
      )
    } catch (error) {
      // Se der erro no ai-response, continua o fluxo e limpa informações de categoria
      categoryMandatoryInfo.current = ''
      categoryDesiredInfo.current = ''
    } finally {
      setLoading(false)
    }
  }, [dispatch, categoriesList, serviceOrder, setUpCategoryPrompt, getInfoVariables])

  async function submit(data) {
    let payload = data

    payload.ambient = 'backoffice'
    payload.materials = formatMaterialsPayload(data.materials)
    payload.total = calculateTotalValue(payload)

    if (payload?.compartment) {
      dispatch(setCompartmentsList(payload.compartment))
      payload.compartment = payload.compartment.label
    }

    if (budgetResolutionToEdit?.id) payload.id = budgetResolutionToEdit?.id

    if (typeof payload.problem === 'string') payload.problem = { label: payload.problem }

    if (Number(payload.total) === 0) {
      return showToast({
        type: 'error',
        message:
          'Você não pode inserir um problema/serviço onde o total seja zero. Insira materiais ou mão de obra para prosseguir.',
      })
    }

    if (isSingleProblemValueInvalid(payload)) return

    if (!isAgencySaas) {
      await processCategories()
    }

    if (categoryMandatoryInfo.current || categoryDesiredInfo.current) {
      if (!categoryMandatoryInfo.current) isMandatoryInfoRef.current = false
      const res = await handleInfoToValidate(payload?.serviceDescription)
      if (!res) return false
    }

    payload = { ...payload, serviceDescription: getValues('serviceDescription') }

    const formData = createFormDataBudgetResolution(payload)
    setLoading(true)

    return dispatch(updateBudgetResolution(budgetId, formData))
      .then(onSuccess)
      .catch(err => showToast({ type: 'error', message: formatErrorMessage(err) }))
      .finally(() => setLoading(false))
  }

  function onSuccess() {
    const params = { budgetView: true, showOriginalBudget: false }
    dispatch(getBudgetById(budgetId, params)).then(() => {
      setIsEditing(false)
      setBudgetResolutionToEdit(null)
      setResetForm(true)
      showToast({ type: 'success' })
      return handleBack()
    })
  }

  function handleCancel() {
    showDialog({
      type: 'warning',
      subject: 'Você tem certeza que deseja executar esta ação?',
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onApprove: () => {
        closeDialog()
        handleBack()
      },
      onCancel: closeDialog,
    })
  }

  function isSingleProblemValueInvalid(data) {
    const { singleServicePrice, total } = data

    if (singleServicePrice && Number(singleServicePrice) <= total) {
      showDialog({
        type: 'error',
        subject:
          'O valor que você definiu, caso o cliente queira fazer só este serviço, deve ser maior que o total do serviço atual com a mão de obra.',
        onApprove: closeDialog,
      })
      return true
    }
    return false
  }

  const shouldShowImportButton = useMemo(() => {
    return (
      (isTradesman || isSAAS) &&
      [
        STEP_STATUS.WAITING_BUDGET, //
        STEP_STATUS.WAITING_BUDGET_SCHEDULE,
      ].includes(serviceOrder?.stepStatus)
    )
  }, [isTradesman, isSAAS, serviceOrder?.stepDesc])

  const handleImportFilesFromServiceOrder = useCallback(async () => {
    const serviceOrderAttachments = serviceOrder?.attachments?.toJS()

    if (!serviceOrderAttachments?.length) {
      showToast({
        type: 'warning',
        message: 'O chamado não tem mídias para importar',
      })
      return
    }

    try {
      setLoading(true)

      const filesOrNulls = await Promise.all(
        serviceOrderAttachments.map(({ file }) => fetchFileFromUrl(file))
      )

      const importedFiles = filesOrNulls.filter(f => f instanceof File)

      const problemMedia = getValues('problemMedia') || []

      setValue('problemMedia', [...problemMedia, ...importedFiles])

      if (importedFiles.length < serviceOrderAttachments.length) {
        showToast({
          type: 'error',
          message: 'Erro ao importar um ou mais arquivos do chamado',
        })
      } else
        showToast({
          type: 'success',
        })
    } catch (error) {
      showToast({
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [serviceOrder])

  const handleBlur = e => {
    const { name, value } = e.target

    if (!value) {
      setValue(name, 0)
    }
  }

  function handleValidationError() {
    showToast({
      type: 'error',
      message: 'Um ou mais dados do formulário possuem problemas.',
    })
  }

  return (
    <Grid>
      <FormProvider {...methods}>
        <Loader open={loading} hasBackdrop />
        <HeaderTitle title={`Problema / Serviço ${problemNumber}`} backButtonAction={handleBack}>
          {!addProblemReadOnly && (
            <Grid className={styles.buttonsWrapper}>
              {shouldShowImportButton && (
                <Button
                  onClick={handleImportServiceModal}
                  disabled={loading}
                  color="primary"
                  variant="secondary"
                >
                  Importar orçamento
                </Button>
              )}
              <Button onClick={handleSubmit(submit, handleValidationError)} disabled={loading}>
                Salvar
              </Button>
              <Button onClick={handleCancel} disabled={loading} variant="secondary" color="red">
                Cancelar
              </Button>
            </Grid>
          )}
        </HeaderTitle>
        <Grid className={styles.container}>
          <Accordion id="general-data-form" title="Dados gerais" className={styles.columnAccordion}>
            {serviceOrder?.vacancy && (
              <Grid className={styles.formColumns}>
                <CompartmentField />
              </Grid>
            )}
            <Grid className={styles.formColumns}>
              <ProblemField />
              <Grid className={styles.column}>
                <WarrantyMonthsField />
                <MandatoryServiceField />
              </Grid>
            </Grid>
            <Grid className={styles.formColumns}>
              <DiagnosisField />
              <SolutionField />
            </Grid>
          </Accordion>
          <Accordion
            id="description-form"
            title="Descrição detalhada"
            className={styles.rowAccordion}
          >
            <Grid className={styles.materialsGridContainer} style={{ flex: '0 0 70%' }}>
              <Grid className={styles.formColumns}>
                <MaterialTable />
              </Grid>
              <SingleServicePrice handleBlur={handleBlur} />
            </Grid>
            <Grid className={styles.materialsGridContainer}>
              <Grid className={styles.formColumns} style={{ gap: '8px', flex: '0 0 30%' }}>
                <NumericInput
                  label={
                    <span style={{ minHeight: '28px', display: 'block' }}>
                      Valor da mão de obra
                    </span>
                  }
                  name="laborValue"
                  placeholder="R$ 0,00"
                  prefix="R$ "
                  disabled={addProblemReadOnly}
                  onBlur={handleBlur}
                />
                <NumericInput
                  label={
                    <>
                      Quantos <br /> funcionários?
                    </>
                  }
                  name="numberOfProfessionals"
                  disabled={addProblemReadOnly}
                  onBlur={handleBlur}
                />
                <NumericInput
                  label={
                    <>
                      Quanto tempo? <br /> (em horas)
                    </>
                  }
                  name="laborTimeHours"
                  disabled={addProblemReadOnly}
                  onBlur={handleBlur}
                />
              </Grid>
              <Typography className={styles.totalText}>
                Valor Total: R$ {(totalValue ?? 0).toFixed(2)}
              </Typography>
            </Grid>
          </Accordion>
          <Accordion id="attachments" title="Anexos" className={styles.rowAccordion}>
            <div
              id="attachments-section-group"
              style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '8px' }}
            >
              <Button
                className={styles.button}
                variant="ghost"
                disabled={addProblemReadOnly || loading}
                startIcon={
                  <DocumentDownload
                    color={
                      addProblemReadOnly || loading
                        ? Theme.Colors.Grayscale.ThirtyTwo
                        : Theme.Colors.Primary.Base
                    }
                    style={{ fontSize: '24px' }}
                  />
                }
                onClick={handleImportFilesFromServiceOrder}
              >
                Importar anexos do chamado
              </Button>
              <AttachmentsField />
            </div>
          </Accordion>
        </Grid>
      </FormProvider>
      {aiValidationModalInfo.open && (
        <AiValidationModal
          modalInfo={aiValidationModalInfo}
          onClose={() => setAiValidationModalInfo(AI_VALIDATION_MODAL_INITIAL_STATE)}
          problem={referaProblem}
          solution={getValues('serviceDescription')}
          modelSlug={modelSlug}
          serviceOrderId={serviceOrder?.id}
          budgetResolutionId={budgetResolutionToEdit?.id}
        />
      )}
      <ImportServiceModal
        open={importServiceModalOpen}
        handleConfirm={handleImportService}
        handleModal={() => handleImportServiceModal(prevState => !prevState)}
      />
    </Grid>
  )
}
